import { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import {
  GetWaterProfilesByAccount,
  AlterActiveState,
} from "../../services/ClientWaterProfileService";
import CommonLoader from "../common/CommonLoader";
import SelectOrders from "./SelectOrders"; // Adjust path as needed
import ComparisonArea from "./ComparisonArea"; // Adjust path as needed

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faArrowLeft,
  faBalanceScale,
  faPlus,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";

const WaterProfileComparison = ({ accountNumber }) => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "accountNumber", label: "Account" },
    { id: "type", label: "Type" },
    { id: "calcium", label: "Calcium" },
    { id: "totalAlkalinity", label: "Total Alkalinity" },
    { id: "sulfate", label: "Sulfate" },
    { id: "magnesium", label: "Magnesium" },
    { id: "totalHardness", label: "Total Hardness" },
    { id: "pH", label: "pH Level" },
    { id: "totalChlorine", label: "Total Chlorine" },
    { id: "chloride", label: "Chloride" },
    { id: "sodium", label: "Sodium" },
    { id: "tds", label: "TDS" },
    { id: "actions", label: "Actions" },
  ];

  const chemicalFields = [
    "calcium",
    "totalAlkalinity",
    "sulfate",
    "magnesium",
    "totalHardness",
    "pH",
    "totalChlorine",
    "chloride",
    "sodium",
    "tds",
  ];

  const rowsPerPageOptions = [5, 10, 25, 50, 100, 200];

  const [profilesData, setProfilesData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showComparisonArea, setShowComparisonArea] = useState(false);
  const [comparisonData, setComparisonData] = useState({
    profile: null,
    orders: []
  });

  useEffect(() => {
    fetchProfiles();
  }, [accountNumber]);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setPaginatedData(profilesData.slice(start, end));
  }, [profilesData, page, rowsPerPage]);

  const fetchProfiles = async () => {
    if (accountNumber == undefined) {
      return;
    }
    try {
      setLoading(true);
      const response = await GetWaterProfilesByAccount(accountNumber);
      setProfilesData(response.results || []);
      setPaginatedData(response.results || []);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      NotificationManager.error("Error fetching profiles", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setShowComparisonArea(false);
    setComparisonData({ profile: null, orders: [] });
    setSelectedProfile(null);
    setSelectedOrders([]);
  };

  const handleToggleActive = async (profileId) => {
    const profile = profilesData.find(p => p.id === profileId);
    if (profile) {
      setSelectedProfile(profile);
      setShowOrderDialog(true);
    }
  };

  const handleOrderSelection = (orderNumbers) => {
    if (selectedProfile && orderNumbers.length > 0) {
      setComparisonData({
        profile: selectedProfile,
        orders: orderNumbers
      });
      setSelectedOrders(orderNumbers);
      setShowOrderDialog(false);
      setShowComparisonArea(true);
      
    }
  };

  const handleCloseOrderDialog = () => {
    setShowOrderDialog(false);
    setSelectedProfile(null);
  };

  const renderActions = (profile) => (
    <div
      className="buttons are-small has-addons"
      style={{ minWidth: "120px", display: "flex", gap: "4px" }}
    >
      <button
        className="button is-small is-primary is-outlined"
        onClick={() => handleToggleActive(profile.id)}
        title="Compare Profile"
      >
        <span className="icon is-small">
          <FontAwesomeIcon icon={faBalanceScale} />
        </span>
      </button>
    </div>
  );

  const totalPages = Math.ceil(profilesData.length / rowsPerPage);

  // Show comparison area if active
  if (showComparisonArea) {
    return (
      <div className="container">
        <div className="section" style={{ padding: "0" }}>
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <button
                  className="button is-light"
                  onClick={handleBackClick}
                  title="Back to Profile List"
                >
                  <span className="icon">
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </span>
                  <span>Back to Profiles</span>
                </button>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <h1 className="title is-4 has-text-primary">
                  <FontAwesomeIcon icon={faBalanceScale} className="mr-2" />
                  Water Profile Comparison
                </h1>
              </div>
            </div>
          </div>
          
          <hr className="has-background-primary" style={{ height: "2px" }} />
          
          <ComparisonArea
            selectedWaterProfile={comparisonData.profile}
            selectedOrders={comparisonData.orders}
            accountNumber={accountNumber}
            onBack={handleBackClick}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="section" style={{ padding: "0" }}>
        <div className="level">
          <div className="level-left">
            <div className="level-item">
              <h1 className="title is-3 has-text-primary">
                <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                Water Profile Comparison System
              </h1>
            </div>
          </div>
        </div>

        <hr className="has-background-primary" style={{ height: "2px" }} />

        {/* Selected Orders Summary - Only show if there are selected orders */}
        {selectedOrders.length > 0 && !showComparisonArea && (
          <div className="notification is-light is-info mb-4">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <strong>Last Comparison:</strong>
                  <span className="ml-2">
                    {selectedOrders.map((orderNum, index) => (
                      <span key={orderNum}>
                        <span className="tag is-info is-light">#{orderNum}</span>
                        {index < selectedOrders.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <span className="tag is-primary">{selectedOrders.length} orders</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          {/* Enhanced table container with sticky header and scrolling */}
          <div
            className="table-container"
            style={{
              maxHeight: "600px",
              overflowY: "auto",
              border: "1px solid #e8e8e8",
              borderRadius: "8px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            }}
          >
            <table
              className="table is-fullwidth is-striped is-hoverable"
              style={{
                marginBottom: "0",
                borderCollapse: "separate",
                borderSpacing: "0",
              }}
            >
              <thead>
                <tr
                  className="has-background-primary-light"
                  style={{
                    position: "sticky",
                    top: "0",
                    zIndex: "10",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  {columns.map((col, index) => (
                    <th
                      key={col.id}
                      className="has-text-weight-bold has-text-dark"
                      style={{
                        borderBottom: "3px solid #3273dc",
                        borderRight:
                          index < columns.length - 1
                            ? "1px solid rgba(0,0,0,0.08)"
                            : "none",
                        padding: "14px 10px",
                        backgroundColor: "#ebf3fd",
                        whiteSpace: "nowrap",
                        minWidth: col.id === "actions" ? "120px" : "auto",
                        fontSize: "0.875rem",
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((profile, rowIndex) => (
                    <tr
                      key={profile.id}
                      className={
                        rowIndex % 2 === 0 ? "has-background-white" : "has-background-white-ter"
                      }
                      style={{
                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      {columns.slice(0, -1).map((col, colIndex) => (
                        <td
                          key={col.id}
                          className="has-text-weight-normal"
                          style={{
                            padding: "12px 10px",
                            borderRight:
                              colIndex < columns.length - 2
                                ? "1px solid rgba(0,0,0,0.05)"
                                : "none",
                            whiteSpace: "nowrap",
                            fontSize: "0.875rem",
                          }}
                        >
                          {chemicalFields.includes(col.id) ? (
                            <span className="tag is-light is-rounded">
                              {profile[col.id]?.toFixed(2) || "N/A"}
                            </span>
                          ) : (
                            profile[col.id] || "N/A"
                          )}
                        </td>
                      ))}
                      <td
                        style={{
                          padding: "12px 10px",
                          position: "sticky",
                          right: "0",
                          backgroundColor:
                            rowIndex % 2 === 0 ? "#ffffff" : "#fafafa",
                          borderLeft: "2px solid rgba(50, 115, 220, 0.1)",
                          zIndex: "5",
                        }}
                      >
                        {renderActions(profile)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="has-text-centered has-text-grey-light"
                      style={{ padding: "60px 20px" }}
                    >
                      {loading ? (
                        <div>
                          <span className="icon is-large has-text-primary">
                            <i className="fas fa-spinner fa-pulse"></i>
                          </span>
                          <p className="mt-3 title is-5">Loading profiles...</p>
                        </div>
                      ) : (
                        <div>
                          <span className="icon is-large has-text-grey">
                            <FontAwesomeIcon icon={faEye} />
                          </span>
                          <p className="mt-3 title is-5">No water profiles found</p>
                          <p className="subtitle is-6">
                            Water profiles will appear here when available
                          </p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {profilesData.length > 0 && (
            <div className="level mt-5">
              <div className="level-left">
                <div className="level-item">
                  <div className="field has-addons">
                    <div className="control">
                      <span className="button is-static is-rounded">Rows per page:</span>
                    </div>
                    <div className="control">
                      <div className="select is-rounded">
                        <select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                          }}
                        >
                          {rowsPerPageOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="level-item">
                  <p className="has-text-grey is-size-7">
                    Showing {page * rowsPerPage + 1}-
                    {Math.min((page + 1) * rowsPerPage, profilesData.length)} of{" "}
                    {profilesData.length}
                  </p>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <nav className="pagination is-rounded" role="navigation">
                    <button
                      className="pagination-previous"
                      disabled={page === 0}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      className="pagination-next"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                    <ul className="pagination-list">
                      {Array.from(
                        { length: Math.min(totalPages, 5) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i;
                          } else if (page < 3) {
                            pageNum = i;
                          } else if (page >= totalPages - 3) {
                            pageNum = totalPages - 5 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }

                          return (
                            <li key={pageNum}>
                              <button
                                className={`pagination-link ${
                                  page === pageNum ? "is-current" : ""
                                }`}
                                onClick={() => setPage(pageNum)}
                              >
                                {pageNum + 1}
                              </button>
                            </li>
                          );
                        }
                      )}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Select Orders Dialog */}
        <SelectOrders
          open={showOrderDialog}
          onClose={handleCloseOrderDialog}
          onDone={handleOrderSelection}
          accountNumber={accountNumber}
          title="Select Orders for Water Profile Comparison"
          maxSelection={10}
        />

        <CommonLoader loading={loading} />
      </div>
    </div>
  );
};

export default WaterProfileComparison;